import type { Chat } from '@ai-sdk/react';
import type { AcpSessionChangeData, CrowdexUIMessage } from '@crowdex/agent-contracts';
import type {
  ConversationHandoffRequest,
  ConversationHandoffResult,
  HandoffCheckout,
  HandoffPlacement,
} from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import { forgeHostOfRemote } from '@crowdex/workspace-runtime/forge';
import type { ExecutionMode } from '@/components/worktree-controls';
import { associateDesktopWorkspace, ensureCloudLogicalWorkspace } from '@/hooks/use-message-sync';
import { type CloudRuntime, snapshotCloudTask } from '@/lib/cloud-runtime/cloud-runtime';
import { conversationHandoffContext } from '@/lib/conversations/conversation-handoff';
import { basename } from '@/lib/desktop-paths';
import { resolveAgentForRuntime, resolvePersistedAgent } from '@/lib/desktop-shell/cloud-agents';
import {
  type CloudHandoffSource,
  cloudHandoffBranchName,
  usesWorkspaceSnapshot,
} from '@/lib/desktop-shell/cloud-handoff';
import type { ConversationScopes } from '@/lib/desktop-shell/conversation-scope';
import { workspaceName } from '@/lib/desktop-shell/conversation-worktree';
import { loadExecutionTargets } from '@/lib/desktop-shell/execution-targets';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { localWorkspaceAssociation } from '@/lib/desktop-shell/workspace-replicas';
import { errorMessage } from '@/lib/error-message';
import { type NativeAgent, type NativeGitStatus, readNativeGitStatus } from '@/lib/native-desktop';
import { withSessionEvent } from '@/lib/session-events';
import { sourceControlRepositoryFullName } from '@/lib/sidebar-threads';
import {
  checkoutSourceControlRepository,
  type SourceControlRepository,
} from '@/lib/source-control-connector';

/** Direction of an agent handoff: a same-environment agent swap or crossing the local/cloud boundary. */
export type AgentHandoffDirection =
  | 'agent'
  | 'local-to-cloud'
  | 'cloud-to-local'
  | 'worktree-to-local';

/**
 * The user-facing environment labels recorded on the "environment" session
 * event when handing a conversation off. `previousEnv` reflects the runtime the
 * conversation is leaving; `nextEnv` the runtime it lands on (unchanged for a
 * same-environment agent swap). Pure so it can be unit tested and shared.
 */
export function handoffEnvironments(input: {
  executionMode: ExecutionMode;
  direction: AgentHandoffDirection;
}): { previousEnv: 'Cloud' | 'Local' | 'Worktree'; nextEnv: 'Cloud' | 'Local' | 'Worktree' } {
  const previousEnv =
    input.direction === 'worktree-to-local'
      ? 'Worktree'
      : input.executionMode === 'cloud'
        ? 'Cloud'
        : input.executionMode === 'worktree'
          ? 'Worktree'
          : 'Local';
  const nextEnv =
    input.direction === 'local-to-cloud'
      ? 'Cloud'
      : input.direction === 'cloud-to-local'
        ? 'Local'
        : input.direction === 'worktree-to-local'
          ? 'Local'
          : previousEnv;
  return { previousEnv, nextEnv };
}

/**
 * What Cloud checks out for a local conversation. The live working tree
 * (`usesWorkspaceSnapshot`) is sent as a snapshot against `baseRef`, published
 * on the current branch when there is one; otherwise Cloud checks out the named
 * remote branch. The server captures the snapshot itself. Pure so the
 * snapshot-vs-remote decision can be unit tested.
 */
export function cloudHandoffCheckout(input: {
  source: CloudHandoffSource;
  branch: string;
  baseRef: string;
  useWorkspaceSnapshot: boolean;
}): HandoffCheckout {
  if (input.useWorkspaceSnapshot) {
    return {
      type: 'snapshot',
      baseRef: input.baseRef,
      ...(input.branch.trim().length === 0 ? {} : { branch: input.branch }),
    };
  }
  return { type: 'remote', ref: cloudHandoffBranchName(input.source) ?? 'HEAD' };
}

/** Hands the conversation to another placement; the server owning it does the work. */
async function requestHandoff(
  request: ConversationHandoffRequest,
): Promise<ConversationHandoffResult> {
  const result = await apiFetch<ConversationHandoffResult>('/api/handoffs', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });
  return result;
}

export type AgentHandoff = {
  completeAgentHandoff: (input: {
    target: NativeAgent;
    nextWorkspace: string | null;
    direction: 'agent' | 'worktree-to-local';
    repository?: string | null;
    branch?: string | null;
    modelChange?: AgentModelHandoff;
  }) => Promise<void>;
  requestContinueInCloud: (selected: { id: string }, targetAgentId?: string) => Promise<void>;
  handOffToCloud: (input: {
    /** The folder's repository on a forge, when it has one; null for a folder with no remote. */
    repository: { host: string; fullName: string } | null;
    checkout: HandoffCheckout;
    pullRequest: PullRequestSummary | null;
  }) => Promise<void>;
  provisionCloudWorkspace: () => Promise<boolean>;
  continueLocally: () => Promise<void>;
  selectAgent: (nextId: string, modelChange?: AgentModelHandoff) => Promise<boolean>;
};

type AgentModelHandoff = {
  modelId: string;
  from: string | null;
  to: string;
};

/**
 * Agent handoff / cloud provisioning for the desktop shell: swapping agents,
 * handing a conversation off between local and cloud runtimes, and provisioning
 * the cloud workspace.
 *
 * A handoff between this machine and Cloud is one request to this machine's
 * server (`POST /api/handoffs`), which moves the conversation's rows, its
 * workspace checkpoint and its agent's session; the page only registers the
 * workspace around it and adopts the result.
 *
 * Every value the original inline closures read (conversation/git/agent state)
 * is passed in as a render-time snapshot rather than read via
 * `store.getState()`, because several of these functions read that state *after*
 * awaiting async work and the original closures captured the pre-await render
 * values — most importantly `executionMode`, which drives the recorded
 * "environment" session-event label. Only the (stable) store setters are pulled
 * from the store. The cloud-handoff-dialog fields, mutable refs, and sibling
 * worktree/repository callbacks are injected because they are not owned by the
 * store. A handoff belongs to the conversation it started in: once it settles, the shell follows
 * only while that conversation is still shown, and its cached copy, which it is restored from,
 * always does.
 */
export function createAgentHandoff(deps: {
  store: DesktopShellStore;
  conversations: ConversationScopes;
  executionMode: ExecutionMode;
  chatId: string;
  messageRevision: number;
  workspace: string | null;
  gitStatus: NativeGitStatus | null;
  agents: readonly NativeAgent[];
  agentId: string;
  localHandoffAgentId: string | null;
  localHandoffWorking: boolean;
  messages: readonly CrowdexUIMessage[];
  agent: NativeAgent | null;
  conversationTitle: string;
  modelId: string;
  cloudAgentSelected: boolean;
  cloudRuntime: CloudRuntime | null;
  busy: boolean;
  cloudHandoffWorking: boolean;
  cloudHandoffSource: CloudHandoffSource;
  cloudHandoffRepository: SourceControlRepository | null;
  cloudHandoffAgentId: string | null;
  setCloudHandoffSource: (source: CloudHandoffSource) => void;
  setCloudHandoffAgentId: (id: string | null) => void;
  setCloudHandoffWorking: (working: boolean) => void;
  setCloudHandoffError: (error: string | null) => void;
  chatStoreRef: { current: Map<string, Chat<CrowdexUIMessage>> };
  cloudRuntimeRef: { current: CloudRuntime | null };
  cloudDisconnectedRef: { current: boolean };
  pendingExecutionAfterLocalRef: { current: ExecutionMode | null };
  prepareManagedWorktree: (sourceRoot?: string | null) => Promise<boolean>;
  resolveCloudHandoffRepository: () => Promise<SourceControlRepository | null>;
  chooseWorkspaceForHandoff: (input: {
    logicalWorkspaceId: string;
    name?: string;
  }) => Promise<string | null>;
}): AgentHandoff {
  const { store } = deps;
  /** The conversation this render hands off. */
  const conversation = deps.conversations.of(deps.chatId);

  /** Gives the workspace its synchronized identity in the cloud. */
  async function registerLocalWorkspace(
    workspaceRoot: string,
    preferredLogicalWorkspaceId?: string,
  ): Promise<string> {
    const { setSyncedWorkspaces } = store.getState();
    let registered = store
      .getState()
      .syncedWorkspaces.find(
        (candidate) => candidate.rootPath === workspaceRoot && candidate.registered === true,
      );
    if (registered?.logicalWorkspaceId === undefined) {
      // The server names it (a worktree after its repository); the cloud gets that name.
      registered = await associateDesktopWorkspace(
        workspaceRoot,
        preferredLogicalWorkspaceId === undefined
          ? {}
          : { logicalWorkspaceId: preferredLogicalWorkspaceId },
      );
      setSyncedWorkspaces((current) => [
        registered as NonNullable<typeof registered>,
        ...current.filter((candidate) => candidate.id !== registered?.id),
      ]);
    }
    const logicalWorkspaceId = registered.logicalWorkspaceId;
    if (logicalWorkspaceId === undefined)
      throw new Error('The workspace has no synchronized identity.');
    await ensureCloudLogicalWorkspace(logicalWorkspaceId, registered.name);
    return logicalWorkspaceId;
  }

  function sessionChanges(input: {
    target: NativeAgent;
    direction: AgentHandoffDirection;
    modelChange?: AgentModelHandoff;
    /** The environment's name when it is another server of the session. */
    environment?: string;
  }): AcpSessionChangeData[] {
    const { previousEnv, nextEnv } = handoffEnvironments({
      executionMode: deps.executionMode,
      direction: input.direction,
    });
    return [
      { field: 'agent', from: deps.agent?.name ?? null, to: input.target.name },
      { field: 'environment', from: previousEnv, to: input.environment ?? nextEnv },
      ...(input.modelChange === undefined
        ? []
        : [{ field: 'model' as const, from: input.modelChange.from, to: input.modelChange.to }]),
    ];
  }

  /** Adopts the conversation on its new placement: messages, agent, workspace, runtime. */
  function switchConversation(input: {
    target: NativeAgent;
    nextWorkspace: string | null;
    direction: AgentHandoffDirection;
    messages: CrowdexUIMessage[];
    messageRevision: number;
    modelId: string;
    repository?: string | null;
    branch?: string | null;
    agentSessionMoved?: boolean;
    destinationWorkspaceId?: string;
    /** Whether Crowdex Cloud owns the conversation now. */
    cloud?: boolean;
    /** Where it runs there, when the direction does not say. */
    runtime?: 'local' | 'worktree' | 'cloud';
  }): void {
    const { setInitialMessages, setConversationCache, setSyncedThreads } = store.getState();
    const runtime =
      input.runtime ??
      (input.direction === 'local-to-cloud'
        ? 'cloud'
        : input.direction === 'agent'
          ? deps.executionMode
          : 'local');
    if (input.direction !== 'agent') {
      setSyncedThreads((current) =>
        current.map((entry) => {
          if (entry.id !== deps.chatId) return entry;
          const { onCloud: _previous, ...rest } = entry;
          return {
            ...rest,
            runtime: runtime === 'cloud' ? 'cloud' : runtime,
            ...(input.destinationWorkspaceId === undefined
              ? {}
              : { workspaceId: input.destinationWorkspaceId }),
            ...((input.cloud ?? entry.onCloud === true) ? { onCloud: true as const } : {}),
          };
        }),
      );
    }
    // Its chat starts again from the moved transcript, when it is shown.
    setInitialMessages(deps.chatId, input.messages);
    deps.chatStoreRef.current.delete(deps.chatId);
    // Its next session starts from what it said with the agent before.
    conversation.update({
      pendingSend: null,
      cloudHandoffContext:
        deps.messages.length === 0
          ? null
          : conversationHandoffContext({
              messages: deps.messages,
              sourceAgent: deps.agent?.name ?? 'Unknown agent',
              targetAgent: input.target.name,
              repository: input.repository,
              branch: input.branch,
              direction: input.direction,
              agentSessionMoved: input.agentSessionMoved === true,
            }),
    });
    conversation.whileShown((state) => {
      state.setRestoredMessageCount(input.messages.length);
      state.setRestoredModelId(input.modelId || null);
      deps.cloudRuntimeRef.current = null;
      deps.cloudDisconnectedRef.current = false;
      state.setCloudRuntime(null);
      if (input.direction !== 'agent') state.setExecutionMode(runtime);
      state.setAgentId(input.target.id);
      state.setWorkspace(input.nextWorkspace);
      state.setAgentHandoffError(null);
    });
    setConversationCache((current) => ({
      ...current,
      [deps.chatId]: {
        id: deps.chatId,
        agentId: input.target.id,
        title: deps.conversationTitle,
        runtime,
        workspaceRoot: input.nextWorkspace,
        messages: input.messages,
        ...(input.modelId.length === 0 ? {} : { modelId: input.modelId }),
        updatedAt: Date.now(),
        restoredMessageCount: input.messages.length,
        messageRevision: input.messageRevision,
      },
    }));
  }

  /** An agent swap or a move to the primary checkout: the conversation stays on this server. */
  async function completeAgentHandoff(input: {
    target: NativeAgent;
    nextWorkspace: string | null;
    direction: 'agent' | 'worktree-to-local';
    repository?: string | null;
    branch?: string | null;
    modelChange?: AgentModelHandoff;
  }): Promise<void> {
    // Another agent has its own models: the one picked for this agent is not its (Claude Code's
    // Opus would show as Codex's while Codex's list loads). The same agent keeps it.
    const modelId = input.modelChange?.modelId ?? (input.direction === 'agent' ? '' : deps.modelId);
    const messages = withSessionEvent(
      deps.messages,
      sessionChanges(input),
      modelId.length === 0 ? undefined : { modelId },
    );
    switchConversation({
      ...input,
      messages,
      messageRevision: deps.messageRevision,
      modelId,
    });
  }

  function handoffRequest(input: {
    target: NativeAgent;
    direction: 'local-to-cloud' | 'cloud-to-local';
    environment?: string;
    cloud: boolean;
    placement: HandoffPlacement;
    checkout?: HandoffCheckout;
  }): ConversationHandoffRequest {
    return {
      conversationId: deps.chatId,
      target: { cloud: input.cloud, placement: input.placement },
      ...(input.checkout === undefined ? {} : { checkout: input.checkout }),
      agent:
        input.direction === 'cloud-to-local'
          ? input.target
          : { id: input.target.id, name: input.target.name },
      ...(deps.modelId.length === 0 ? {} : { modelId: deps.modelId }),
      changes: sessionChanges({
        target: input.target,
        direction: input.direction,
        ...(input.environment === undefined ? {} : { environment: input.environment }),
      }),
    };
  }

  /** The forge repository a remote URL names (`host` + `owner/name`), or null. */
  function remoteRepository(remoteUrl: string | null): { host: string; fullName: string } | null {
    const host = forgeHostOfRemote(remoteUrl);
    const fullName = sourceControlRepositoryFullName(remoteUrl);
    return host === null || fullName === null ? null : { host, fullName };
  }

  async function handOffToCloud(input: {
    repository: { host: string; fullName: string } | null;
    checkout: HandoffCheckout;
    pullRequest: PullRequestSummary | null;
  }): Promise<void> {
    const { setSyncedWorkspaces, remoteExecutionTarget } = store.getState();
    const target =
      resolvePersistedAgent(deps.agents, deps.cloudHandoffAgentId) ??
      resolveAgentForRuntime(deps.agents, deps.agent?.command, 'cloud');
    if (target === undefined) throw new Error('No cloud agent is configured.');
    const result = await requestHandoff(
      handoffRequest({
        target,
        direction: 'local-to-cloud',
        cloud: remoteExecutionTarget?.cloud ?? false,
        placement: {
          kind: 'remote',
          ...(remoteExecutionTarget === null
            ? {}
            : {
                providerId: remoteExecutionTarget.providerId,
                profileId: remoteExecutionTarget.profileId,
              }),
          ...(input.repository === null
            ? {}
            : {
                repository: { host: input.repository.host, fullName: input.repository.fullName },
              }),
          pullRequest: input.pullRequest,
        },
        checkout: input.checkout,
      }),
    );
    setSyncedWorkspaces((current) => [
      {
        id: result.workspaceId,
        logicalWorkspaceId: result.logicalWorkspaceId,
        registered: true,
        runtime: 'cloud',
        rootPath: result.workspaceRoot,
        // Its project's name, as the server named the folder it came from.
        name:
          deps.workspace === null
            ? basename(result.workspaceRoot)
            : workspaceName(current, deps.workspace),
        availableOnDevice: true,
      },
      ...current.filter((candidate) => candidate.id !== result.workspaceId),
    ]);
    switchConversation({
      target,
      nextWorkspace: result.workspaceRoot,
      direction: 'local-to-cloud',
      messages: result.messages as CrowdexUIMessage[],
      messageRevision: result.messageRevision,
      modelId: deps.modelId,
      repository: input.repository?.fullName ?? null,
      branch: result.branch,
      agentSessionMoved: result.agentSessionMoved,
      cloud: result.cloud,
    });
    conversation.whileShown((state) => state.setGitStatus(null));
    deps.setCloudHandoffAgentId(null);
  }

  async function requestContinueInCloud(
    selected: { id: string },
    targetAgentId?: string,
  ): Promise<void> {
    const { setExecutionMode } = store.getState();
    if (
      selected.id !== deps.chatId ||
      deps.workspace === null ||
      deps.gitStatus === null ||
      deps.executionMode === 'cloud' ||
      deps.cloudHandoffWorking
    ) {
      return;
    }
    const gitStatus = deps.gitStatus;
    const workspace = deps.workspace;
    const previousExecutionMode = deps.executionMode;
    deps.setCloudHandoffAgentId(targetAgentId ?? null);
    deps.setCloudHandoffWorking(true);
    deps.setCloudHandoffError(null);
    setExecutionMode('cloud');
    try {
      // Without a chosen target, the first remote host: this server's, then the cloud's.
      if (store.getState().remoteExecutionTarget === null) {
        const [remote] = await loadExecutionTargets();
        if (remote === undefined) {
          throw new Error(
            'Sign in to Crowdex Cloud in Settings → Account to send conversations to the cloud.',
          );
        }
        conversation.whileShown((state) => state.setRemoteExecutionTarget(remote));
      }
      const [repository] = await Promise.all([
        deps.resolveCloudHandoffRepository(),
        registerLocalWorkspace(workspace),
      ]);
      // Without a forge the cloud reaches, the folder goes as it is (its base commit with it).
      const useWorkspaceSnapshot =
        repository === null || usesWorkspaceSnapshot(deps.cloudHandoffSource, gitStatus.branch);
      await handOffToCloud({
        repository: repository ?? remoteRepository(gitStatus.remoteUrl),
        checkout: cloudHandoffCheckout({
          source: deps.cloudHandoffSource,
          branch: gitStatus.branch,
          baseRef:
            gitStatus.pullRequest?.baseBranch ||
            deps.cloudHandoffRepository?.defaultBranch ||
            gitStatus.branch ||
            'HEAD',
          useWorkspaceSnapshot,
        }),
        pullRequest: useWorkspaceSnapshot ? gitStatus.pullRequest : null,
      });
      deps.setCloudHandoffSource({
        type: 'remote',
        branch:
          cloudHandoffBranchName(deps.cloudHandoffSource) ??
          (gitStatus.branch.trim() || repository?.defaultBranch || 'HEAD'),
      });
    } catch (reason) {
      conversation.place({ executionMode: previousExecutionMode });
      deps.setCloudHandoffError(errorMessage(reason));
    } finally {
      deps.setCloudHandoffWorking(false);
    }
  }

  async function provisionCloudWorkspace(): Promise<boolean> {
    const workspace = deps.workspace;
    const gitStatus = deps.gitStatus;
    if (workspace === null) return false;
    // A cloud project is the cloud's already; only a Git folder of this machine goes with it.
    if (workspace.startsWith('/workspace') || gitStatus === null) return true;
    conversation.update({ cloudRepositoryBusy: true });
    try {
      const chosen = cloudHandoffBranchName(deps.cloudHandoffSource);
      const currentBranch = gitStatus.branch.trim();
      if (chosen !== null && chosen !== currentBranch) {
        // Another branch than the folder's: from the repository's forge.
        const repository = await deps.resolveCloudHandoffRepository();
        if (repository === null)
          throw new Error(
            `No code forge reaches this repository from the cloud: switch to ${chosen} here, or send the folder as it is.`,
          );
        conversation.place({
          workspace: await checkoutSourceControlRepository(
            repository,
            deps.chatId,
            { type: 'remote', ref: chosen },
            null,
          ),
        });
      } else {
        // The folder as it is (its changes and its base commit): no forge account needed.
        const sent = await apiFetch<{ workspaceRoot: string }>('/api/local/cloud-workspaces', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            conversationId: deps.chatId,
            workspaceRoot: workspace,
            workspaceName: workspaceName(store.getState().syncedWorkspaces, workspace),
            baseRef: currentBranch || 'HEAD',
            title: 'New conversation',
            repository: remoteRepository(gitStatus.remoteUrl),
          }),
        });
        conversation.place({ workspace: sent.workspaceRoot });
      }
      conversation.whileShown((state) => state.setGitStatus(null));
      return true;
    } catch (reason) {
      conversation.whileShown((state) => state.setCloudProvisionError(errorMessage(reason)));
      deps.setCloudHandoffError(errorMessage(reason));
      return false;
    } finally {
      conversation.update({ cloudRepositoryBusy: false });
    }
  }

  async function continueLocally(): Promise<void> {
    const {
      setAgentHandoffError,
      setLocalHandoffWorking,
      setLocalHandoffOpen,
      setLocalHandoffAgentId,
      syncedWorkspaces,
    } = store.getState();
    const target = deps.agents.find((candidate) => candidate.id === deps.localHandoffAgentId);
    let localAssociation = localWorkspaceAssociation(deps.workspace, syncedWorkspaces);
    if (target === undefined) {
      setAgentHandoffError('No local agent is available.');
      return;
    }
    if (localAssociation === undefined) {
      const source = syncedWorkspaces.find((candidate) => candidate.rootPath === deps.workspace);
      if (source?.logicalWorkspaceId === undefined) {
        setAgentHandoffError('This cloud conversation has no project identity.');
        return;
      }
      const selectedPath = await deps.chooseWorkspaceForHandoff({
        logicalWorkspaceId: source.logicalWorkspaceId,
        name: source.name,
      });
      if (selectedPath === null) return;
      localAssociation = store
        .getState()
        .syncedWorkspaces.find((candidate) => candidate.rootPath === selectedPath);
    }
    const localWorkspace = localAssociation?.rootPath;
    if (localWorkspace === undefined) {
      setAgentHandoffError('The selected local workspace could not be saved.');
      return;
    }
    setLocalHandoffWorking(true);
    setAgentHandoffError(null);
    try {
      const sourceLogicalWorkspaceId = syncedWorkspaces.find(
        (candidate) =>
          candidate.rootPath === deps.workspace &&
          candidate.registered === true &&
          candidate.logicalWorkspaceId !== undefined,
      )?.logicalWorkspaceId;
      const result = await requestHandoff(
        handoffRequest({
          target,
          direction: 'cloud-to-local',
          // This session's server receives the conversation in a worktree of this repository:
          // the user's folder is never changed.
          cloud: false,
          placement: {
            kind: 'worktree',
            workspaceRoot: localWorkspace,
            workspaceName: workspaceName(syncedWorkspaces, localWorkspace),
          },
        }),
      );
      await registerLocalWorkspace(
        result.workspaceRoot,
        localAssociation?.logicalWorkspaceId ?? sourceLogicalWorkspaceId,
      );
      switchConversation({
        target,
        nextWorkspace: result.workspaceRoot,
        direction: 'cloud-to-local',
        messages: result.messages as CrowdexUIMessage[],
        messageRevision: result.messageRevision,
        modelId: deps.modelId,
        repository: sourceControlRepositoryFullName(deps.workspace),
        branch: result.branch,
        agentSessionMoved: result.agentSessionMoved,
        destinationWorkspaceId: result.workspaceId,
        cloud: result.cloud,
      });
      const status = await readNativeGitStatus(result.workspaceRoot);
      conversation.whileShown((state) => state.setGitStatus(status));
      setLocalHandoffOpen(false);
      setLocalHandoffAgentId(null);
      // It already runs in a worktree of its own.
      deps.pendingExecutionAfterLocalRef.current = null;
    } catch (error) {
      conversation.whileShown((state) => state.setAgentHandoffError(errorMessage(error)));
    } finally {
      setLocalHandoffWorking(false);
    }
  }

  async function selectAgent(nextId: string, modelChange?: AgentModelHandoff): Promise<boolean> {
    const { setAgentHandoffError } = store.getState();
    if (
      nextId === deps.agentId ||
      deps.busy ||
      deps.cloudHandoffWorking ||
      deps.localHandoffWorking
    ) {
      return false;
    }
    const requested = resolvePersistedAgent(deps.agents, nextId);
    if (requested === undefined) return false;
    const runtime = deps.cloudAgentSelected ? 'cloud' : 'local';
    const target = resolveAgentForRuntime(deps.agents, requested.command, runtime) ?? requested;
    if (target.id === deps.agentId) return false;
    setAgentHandoffError(null);
    try {
      if (runtime === 'cloud' && deps.cloudRuntime !== null) await snapshotCloudTask(deps.chatId);
      await completeAgentHandoff({
        target,
        nextWorkspace: deps.workspace,
        direction: 'agent',
        repository: sourceControlRepositoryFullName(deps.gitStatus?.remoteUrl ?? deps.workspace),
        branch: deps.gitStatus?.branch ?? null,
        ...(modelChange === undefined ? {} : { modelChange }),
      });
      return true;
    } catch (error) {
      conversation.whileShown((state) => state.setAgentHandoffError(errorMessage(error)));
      return false;
    }
  }

  return {
    completeAgentHandoff,
    requestContinueInCloud,
    handOffToCloud,
    provisionCloudWorkspace,
    continueLocally,
    selectAgent,
  };
}
